import { toJS } from "mobx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { makeAd, getOsTypeFromAgent } from "@inferno/renderer-shared-core";
import {
  PWSSessionBackend,
  coastMediaEpisodeCategoriesToGroupSlug,
  PwsMediaConfigResponse,
  PwsViewMediaEpisodeResponse,
} from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { AdPosition } from "../../../../core/page-blocks/Ad.component";
import { Container } from "../../../../core/ui";
import { AxiosLoaderResponse } from "../../lib/coastCommon";
import "./CoastMedia.style.scss";
import { CoastMediaEpisodeNode, CoastMediaTabConfig, episodesToLinkedList } from "./CoastMedia.util";
import { CoastMediaPlayerLoader } from "./CoastMediaPlayerLoader.component";
import { CoastMediaPlayerSkeleton } from "./CoastMediaPlayerSkeleton.component";
import { CoastMediaTabs } from "./CoastMediaTabs.component";
import { coastMediaService } from "../../../../core/services/Pws";

const log = ILog.logger("CoastMedia.component");

type InitialCurEpisodeData = AxiosLoaderResponse<PwsViewMediaEpisodeResponse> | null;
type InitialConfigData = AxiosLoaderResponse<PwsMediaConfigResponse> | null;

interface CoastMediaProps {
  initialCurEpisodeData: InitialCurEpisodeData;
  initialConfigData: InitialConfigData;
  initialEpisodeSlug: string | null;
  initialGroupSlug: string | null;
  session: PWSSessionBackend;
}

/**
 * We do all this linked list madness for tracking cur episode so that we can easier get the next one to play
 */
export const CoastMedia = ({
  initialCurEpisodeData,
  initialEpisodeSlug,
  initialConfigData,
  initialGroupSlug,
  session,
}: CoastMediaProps) => {
  const reactHistory = useHistory();
  const [groupSlug, setGroupSlug] = useState(initialGroupSlug);
  const [playerApiKey, setPlayerApiKey] = useState(
    initialCurEpisodeData?.data?.playerApiKey || initialConfigData?.data?.playerApiKey || null,
  );

  const tabs = useMemo<CoastMediaTabConfig[]>(
    () =>
      initialConfigData?.data?.tabs.map(tab => {
        if (tab.slug === "somewhere-in-time") {
          return {
            ...tab,
            pagination: {
              current: 1,
              previous: null,
              next: null,
              pages: 1,
              per_page: 12,
            },
            enablePagination: false,
            episodesLL: episodesToLinkedList(tab.videos || []),
          };
        }

        return {
          ...tab,
          enablePagination: true,
          episodesLL: episodesToLinkedList(tab.videos || []),
        };
      }) || [],
    [initialConfigData?.data?.tabs],
  );

  const activeTabIdx = useMemo(
    () => Math.max(groupSlug ? tabs.findIndex(tab => tab.slug === groupSlug) : 0, 0),
    [groupSlug, tabs],
  );

  const [currentEpisode, setCurrentEpisode] = useState<CoastMediaEpisodeNode | null>(null);

  const osType = useMemo(() => getOsTypeFromAgent(), []);

  const getNextVideo = useCallback(async () => {
    if (!currentEpisode) {
      log.warn("Can't get next video - current video is not set");
      return null;
    }

    if (currentEpisode.next) {
      return currentEpisode.next;
    }

    try {
      const { video, playerApiKey: playerApiKeyResp } = await coastMediaService.getNextEpisode(
        currentEpisode.data,
        session.state.accessToken,
      );
      if (!playerApiKey) {
        setPlayerApiKey(playerApiKeyResp);
      }

      currentEpisode.next = new CoastMediaEpisodeNode(video);
      return currentEpisode.next;
    } catch (e) {
      log.error("Error fetching next episode", e);
      return null;
    }
  }, [currentEpisode, playerApiKey, session.state.accessToken]);

  const updateCurrentEpisode = useCallback(
    (episode: CoastMediaEpisodeNode, updatedGroupSlug?: string) => {
      if (episode.data.subscriptionRequired && !session.state.hasActiveSubscription) {
        session.executeOnValidSubscription(() => updateCurrentEpisode(episode, updatedGroupSlug));
      } else {
        if (updatedGroupSlug && updatedGroupSlug !== groupSlug) {
          setGroupSlug(updatedGroupSlug);
        }

        setCurrentEpisode(episode);
      }
    },
    [groupSlug, session],
  );

  const playNextEpisode = useCallback(async () => {
    log.debug("Play next episode", toJS(currentEpisode?.data), toJS(currentEpisode?.next?.data));

    const nextEpisode = await getNextVideo();
    if (nextEpisode) {
      updateCurrentEpisode(nextEpisode);
    }
  }, [currentEpisode?.data, currentEpisode?.next?.data, getNextVideo, updateCurrentEpisode]);

  useEffect(() => {
    const [, , pathGroupSlug, pathEpisodeSlug] = reactHistory.location.pathname.split("/");
    log.debug("Should update URL path?", {
      episodeSlug: currentEpisode?.data.slug,
      groupSlug,
      pathGroupSlug,
      pathEpisodeSlug,
    });

    if (currentEpisode && groupSlug && (currentEpisode.data.slug !== pathEpisodeSlug || groupSlug !== pathGroupSlug)) {
      log.debug("Updating URL path");
      const newPath = `/videos/${groupSlug}/${currentEpisode.data.slug}/`;

      // pushing to regular window history (not react hook) avoids whole page re-render
      if (pathEpisodeSlug === "live" || !pathEpisodeSlug) {
        history.replaceState(null, currentEpisode.data.title, newPath);
      } else {
        history.pushState(null, currentEpisode.data.title, newPath);
      }
    }
  }, [currentEpisode, groupSlug, reactHistory.location, initialEpisodeSlug, initialGroupSlug]);

  useEffect(() => {
    const curInitialEpisode = initialCurEpisodeData?.data?.video;
    let episode: CoastMediaEpisodeNode | null;
    let slug = "";

    if (curInitialEpisode) {
      const curEpisodeTab = tabs
        // Figure out which tab the episode belongs to
        .find(tab =>
          curInitialEpisode.categories.some(
            c => !!tab.categories[c.title] && c.value.some(v => tab.categories[c.title] === v),
          ),
        );

      if (curEpisodeTab) {
        slug = curEpisodeTab.slug;
      }

      episode =
        // see if we have it in the list of episodes for that tab
        curEpisodeTab?.episodesLL.find(e => e.id === curInitialEpisode.id) ||
        new CoastMediaEpisodeNode(curInitialEpisode);

      try {
        slug = coastMediaEpisodeCategoriesToGroupSlug(episode.data.categories);
      } catch {
        // do nothing
      }

      return updateCurrentEpisode(episode, slug);
    }

    episode = tabs[activeTabIdx]?.episodesLL.head || null;

    if (tabs[activeTabIdx]?.slug) {
      slug = tabs[activeTabIdx].slug;
    }

    if (episode) {
      return updateCurrentEpisode(episode, slug);
    }
  }, [activeTabIdx, initialCurEpisodeData?.data?.video, tabs, updateCurrentEpisode]);

  return (
    <Container className="component-coast-media">
      {currentEpisode && playerApiKey ? (
        <CoastMediaPlayerLoader
          playNextEpisode={playNextEpisode}
          playerApiKey={playerApiKey}
          episode={currentEpisode.data}
          session={session}
          osType={osType}
        />
      ) : (
        <CoastMediaPlayerSkeleton />
      )}

      <CoastMediaTabs
        setCurrentEpisode={updateCurrentEpisode}
        initialActiveTabIdx={activeTabIdx}
        sessionState={session.state}
        tabs={tabs}
      />

      <section className="ads">
        <AdPosition block={makeAd("3308", 0)} />
        <AdPosition block={makeAd("5052", 0)} />
      </section>
    </Container>
  );
};
